import React, { useEffect } from 'react';
import { DoiNotificationContent } from '@packages/cms-components/src/components/Forms/NewsletterSheet/DoiNotificationContent';
import { useAgnitasData } from '@packages/shared/src/hooks/useAgnitasData';
import { Sheet } from './Sheet';
import { subscribeSheetMessages } from './helpers/messages';
import { NewsletterSubscribeCompact } from './NewsletterSubscribeCompact';
import type { NewsletterSheetCommonProps } from './types';

/**
 * Component for rendering the Newsletter Sheet with Agnitas integration.
 *
 * @param {Object} props - The properties object.
 * @param {IntlShape} props.intl - The internationalization object for formatting messages.
 * @param {NlSheetType | false} [props.nlTexts] - The newsletter sheet texts, if available.
 * @param {boolean} props.loggedIn - Indicates if the user is logged in.
 * @param {boolean} props.useDOI - Indicates if double opt-in is used.
 * @param {Notification} props.notification - The notification object containing visibility and message.
 * @param {React.Dispatch<React.SetStateAction<Notification>>} props.setNotification - Function to set the notification state.
 * @param {React.Dispatch<React.SetStateAction<boolean>>} props.setForceHidden - Function to set the force hidden state.
 * @returns {JSX.Element | null} The rendered component or null if conditions to hide are met.
 */
export const NewsletterSheetAgnitas = ({
  intl,
  nlTexts,
  loggedIn,
  useDOI,
  notification,
  setNotification,
  setForceHidden,
}: NewsletterSheetCommonProps): JSX.Element | null => {
  const agnitasData = useAgnitasData();

  useEffect(() => {
    if (agnitasData?.isPending && agnitasData.email && useDOI && !notification.message) {
      setNotification({
        message: (
          <DoiNotificationContent
            email={agnitasData.email}
            onClickedSuccess={() => setNotification({ ...notification, show: false })}
            onClickedError={(message) => setNotification({ message, show: true })}
          />
        ),
        show: true,
      });
    }
  }, [agnitasData, useDOI, notification, setNotification]);

  const hideSheetLoggedIn =
    loggedIn && (agnitasData === null || agnitasData?.hasPermission || agnitasData?.isPending);

  if (hideSheetLoggedIn) {
    return null;
  }

  return (
    <Sheet
      title={(nlTexts && nlTexts.label) || intl.formatMessage(subscribeSheetMessages.sheetTitle)}
      onClickClose={() => {
        setForceHidden(true);
      }}
    >
      <NewsletterSubscribeCompact />
    </Sheet>
  );
};
